<template>
  <div class="image-preview-list" v-if="imageUrls && imageUrls.length > 0">
    <template v-for="(image, imageIndex) in imageUrls" :key="imageIndex">
      <div class="image-preview-item" v-if="imageIndex === currentImageIndex">
        <v-img
          :src="getImagePreviewUrl(image)"
          contain
          class="preview-image"
          @click="handleImagePreview"
        ></v-img>

        <!-- 图片数量显示 -->
        <!-- <div class="image-count-preview">
          <img class="image-count-icon" src="@/assets/picicon.png" alt="" />
          {{ "共" + imageUrls.length + "张" }}
        </div> -->

        <div class="image-actions-container">
            <!-- 图片操作按钮 -->
            <div class="image-actions">
              <v-icon
                icon="mdi-eye"
                size="20"
                @click.stop="handleImagePreview"
                title="查看图片"
              ></v-icon>
              <img v-if="canDelete"
                src="@/assets/uploadIcon.png"
                alt="上传"
                class="img-icon"
                @click.stop="handleImageUpload"
                title="上传图片"
              />
            </div>

            <!-- 图片切换控制按钮 -->
            <div class="image-navigation" :style="isZoomed ? {scale: '1', left: '50%', transform: 'translateX(-50%)'} : {scale: '0.7', left: '-16px', right: '0'}">
              <div style="display: flex;align-items: center;justify-content: space-between;">
                <v-btn
                  v-if="imageUrls.length > 1"
                  icon="mdi-chevron-left"
                  size="small"
                  variant="text"
                  color="white"
                  class="nav-btn prev-btn"
                  @click.stop="prevImage"
                  :disabled="currentImageIndex === 0"
                  title="上一张"
                ></v-btn>

                <!-- 放大缩小按钮 -->
                <div class="zoom-controls">
                  <v-btn
                    icon="mdi-magnify-plus"
                    size="small"
                    variant="text"
                    color="white"
                    class="zoom-btn"
                    @click.stop="zoomIn"
                    :disabled="isZoomed"
                    title="放大"
                  ></v-btn>
                  <v-btn
                    icon="mdi-magnify-minus"
                    size="small"
                    variant="text"
                    color="white"
                    class="zoom-btn"
                    @click.stop="zoomOut"
                    :disabled="!isZoomed"
                    title="缩小"
                  ></v-btn>
                </div>

                <v-btn
                  v-if="imageUrls.length > 1"
                  icon="mdi-chevron-right"
                  size="small"
                  variant="text"
                  color="white"
                  class="nav-btn next-btn"
                  @click.stop="nextImage"
                  :disabled="currentImageIndex === imageUrls.length - 1"
                  title="下一张"
                ></v-btn>
              </div>
            </div>
        </div>
      </div>
    </template>
  </div>

  <!-- 无图片时的显示 -->
  <div v-else class="no-img" style="line-height: 70px">
    {{ noImageText }}
    <!-- 即使没有图片也显示上传按钮 -->
    <div class="upload-button-container" style="margin-top: 10px;" v-if="canDelete">
      <img
        src="@/assets/uploadIcon.png"
        alt="上传"
        class="img-icon-one"
        @click.stop="handleImageUpload"
        title="上传图片"
      />
    </div>
  </div>

  <!-- 图片预览对话框 -->
  <ImagePreviewDialog
    v-model="showPreview"
    :image-list="imageUrlStrings"
    :initial-index="currentImageIndex"
    :current-material-index="parentIndex"
    :is-delete="canDelete"
    :type="imageType"
    @close="handleClosePreview"
  />

  <!-- 上传对话框 -->
  <UploadImgDialog
    v-model="showUploadDialog"
    mode="new"
    :type="uploadType"
    @upload="handleUploadSuccess"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import ImagePreviewDialog from "./ImagePreviewDialog.vue";
import UploadImgDialog from "./UploadImg.vue";

// 定义组件属性
interface Props {
  imageUrls: (string | File | { url: string; name: string })[];
  parentIndex: number;
  imageType: "product" | "material";
  currentImageIndex: number;
  isZoomed: boolean;
  canDelete?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  imageUrls: () => [],
  parentIndex: 0,
  imageType: "material",
  currentImageIndex: 0,
  isZoomed: false,
});

// 定义组件事件
interface Emits {
  (e: "update:currentImageIndex", index: number): void;
  (e: "zoomIn"): void;
  (e: "zoomOut"): void;
  (
    e: "imagePreview",
    images: { url: string; name: string }[],
    parentIndex: number,
    imageIndex: number,
    canDelete: boolean,
    type: string
  ): void;
  (e: "imageUpload", parentIndex: number, imageIndex: number): void;
  (e: "uploadSuccess", urls: string[], parentIndex: number): void;
}

const emit = defineEmits<Emits>();

// 组件内部状态
const showPreview = ref(false);
const showUploadDialog = ref(false);

// 计算属性
const uploadType = computed(() => {
  return props.imageType === "product" ? "image" : "image";
});

const noImageText = computed(() => {
  // return props.imageType === "product" ? "暂无产品图" : "暂无物料图";
  return ''
});

// 转换图片URL为字符串数组，供ImagePreviewDialog使用
const imageUrlStrings = computed(() => {
  return props.imageUrls.map(item => {
    if (typeof item === "string") {
      return item;
    } else if (item instanceof File) {
      return URL.createObjectURL(item);
    } else if (item && typeof item === "object" && "url" in item) {
      return item.url;
    }
    return "";
  }).filter(url => url !== "");
});

// 监听外部图片索引变化
watch(
  () => props.currentImageIndex,
  (newIndex) => {
    // 可以在这里处理索引变化的逻辑
  }
);

// 获取图片预览URL
const getImagePreviewUrl = (
  file: string | File | { url: string; name: string }
) => {
  if (typeof file === "string") {
    return file;
  } else if (file instanceof File) {
    return URL.createObjectURL(file);
  } else if (file && typeof file === "object" && "url" in file) {
    return file.url;
  }
  return "";
};

// 图片切换方法
const prevImage = () => {
  if (props.currentImageIndex > 0) {
    emit("update:currentImageIndex", props.currentImageIndex - 1);
  }
};

const nextImage = () => {
  if (props.currentImageIndex < props.imageUrls.length - 1) {
    emit("update:currentImageIndex", props.currentImageIndex + 1);
  }
};

// 缩放方法
const zoomIn = () => {
  emit("zoomIn");
};

const zoomOut = () => {
  emit("zoomOut");
};

// 图片预览
const handleImagePreview = () => {
  // 转换为期望的格式
  const images = props.imageUrls.map(item => {
    if (typeof item === "string") {
      return { url: item, name: '' };
    } else if (item instanceof File) {
      return { url: URL.createObjectURL(item), name: item.name };
    } else if (item && typeof item === "object" && "url" in item) {
      return { url: item.url, name: item.name || '' };
    }
    return { url: '', name: '' };
  }).filter(img => img.url !== "");
  
  emit(
    "imagePreview",
    images,
    props.parentIndex,
    props.currentImageIndex,
    props.canDelete || false,
    props.imageType
  );
};

// 关闭预览
const handleClosePreview = () => {
  showPreview.value = false;
};

// 图片上传
const handleImageUpload = () => {
  emit("imageUpload", props.parentIndex, props.currentImageIndex);
  showUploadDialog.value = true;
};

// 上传成功处理
const handleUploadSuccess = (urls: string[]) => {
  emit("uploadSuccess", urls, props.parentIndex);
  showUploadDialog.value = false;
};
</script>

<style scoped lang="scss">
.image-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 auto;
  position: relative;
  width: 100%;
  height: 100%;
}

.image-preview-item {
  //   width: 140px;
  //   height: 140px;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  cursor: pointer;
  border-radius: 4px;
}

.image-preview-item:hover .preview-image {
  transform: scale(1.05);
}

.image-actions {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.image-preview-item:hover .image-actions {
  opacity: 1;
}

.image-actions .v-icon {
  color: #fff;
  cursor: pointer;
}

.image-actions .v-icon:hover {
  color: #ff5252;
}

.img-icon {
  width: 18px;
  height: 13px;
  margin-left: 14px;
  cursor: pointer;
  filter: brightness(0) invert(1);
}

.img-icon-one {
  width: 18px;
  height: 13px;
  cursor: pointer;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  filter: brightness(0) invert(1);
}

.image-count-icon {
  width: 14px;
  height: 14px;
}

.image-navigation {
  position: absolute;
  bottom: 5px;
  // left: 50%;
  // transform: translateX(-50%);
  left: 0;
  right: 0;
  // display: flex;
  // align-items: center;
  // justify-content: space-between;
//   padding: 8px 12px;
  min-width: 116px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.image-preview-item:hover .image-navigation, .image-actions-container:hover {
  opacity: 1;
}

.nav-btn {
  width: 30px !important;
  height: 30px !important;
  background: rgba(43, 45, 51, 0.8) !important;
  border-radius: 50% !important;
  color: white;
}

.nav-btn:hover {
  transform: scale(1.1);
}

.nav-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

.zoom-controls {
  margin: 0 auto;
  display: flex;
  gap: 4px;
}

.zoom-btn {
  width: 30px !important;
  height: 30px !important;
  background: rgba(43, 45, 51, 0.8) !important;
  border-radius: 50% !important;
  color: white;
}

.zoom-btn:hover {
  transform: scale(1.1);
}

.zoom-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

.no-img {
  text-align: center;
  color: #999;
  font-size: 14px;
  line-height: 80px;
  height: 80px!important;
}

/* 深度选择器样式 */
:deep(.mdi:before, .mdi-set) {
  color: #ffffff;
}

.image-navigation :deep(.mdi:before, .mdi-set) {
  font-size: 36px !important;
}

.zoom-controls :deep(.mdi:before, .mdi-set) {
  font-size: 20px !important;
}

.image-actions-container{
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
}
</style>
